import { Link } from "react-router-dom";
import { TRIP, DAYS } from "@/data/itinerary";
import Countdown from "@/components/Countdown";
import WeatherWidget from "@/components/WeatherWidget";
import LiveWindBadge from "@/components/LiveWindBadge";
import { ArrowUpRight, MapPin, Sailboat, Waves, Wind } from "lucide-react";

function formatDate(iso) {
  const d = new Date(iso);
  return d
    .toLocaleDateString("it-IT", { day: "2-digit", month: "long" })
    .replace(".", "");
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen w-full overflow-hidden"
      >
        <div
          className="absolute inset-0 animate-kb"
          style={{
            backgroundImage: `url(${TRIP.heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/45 to-black/75" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 min-h-screen flex flex-col justify-between max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="animate-fade-up">
            <div className="font-accent text-white text-xs">
              22 → 29 Agosto 2026 · Toscana
            </div>
          </div>

          <div className="animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <h1 className="font-display text-white text-6xl md:text-8xl lg:text-9xl leading-[0.95] tracking-tight">
              Isola
              <br />
              <span className="italic text-[#F4C89F]">d'Elba</span>
            </h1>
            <p className="mt-6 max-w-xl text-white/90 text-lg font-light leading-relaxed">
              {TRIP.subtitle}. Sette giorni, otto tappe, un mare che cambia
              colore ad ogni vento.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/itinerario"
                data-testid="hero-cta-itinerary"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#D96C4A] hover:bg-[#b8542f] text-white font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D96C4A]/40"
              >
                Scopri l'itinerario
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link
                to="/checklist"
                data-testid="hero-cta-checklist"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/15 border border-white/30 text-white backdrop-blur hover:bg-white/25 transition-all"
              >
                Bagagli e checklist
              </Link>
            </div>
          </div>

          <div
            className="animate-fade-up flex flex-col md:flex-row md:items-end md:justify-between gap-6"
            style={{ animationDelay: "0.4s" }}
          >
            <Countdown target={`${TRIP.startDate}T15:45:00`} />
            <div className="flex flex-col gap-2">
              <div className="font-accent text-[10px] text-white/85">
                Base della settimana
              </div>
              <a
                href={TRIP.hotel.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-hotel-link"
                className="text-white hover:text-[#F4C89F] transition-colors inline-flex items-center gap-2 font-display text-lg"
              >
                <MapPin className="w-4 h-4" />
                {TRIP.hotel.name}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* GOLDEN RULE */}
      <section
        data-testid="golden-rule-section"
        className="py-24 bg-[#F5EFE6] border-y border-[#1D3A4C]/10"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid gap-10 lg:grid-cols-3 items-center">
          <div>
            <div className="font-accent text-xs text-[#D96C4A] mb-3">
              Prima regola d'oro
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1D3A4C] leading-tight">
              Il vento decide la spiaggia
            </h2>
            <LiveWindBadge className="mt-6" />
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white p-8 border border-[#1D3A4C]/10 hover:shadow-md transition-shadow">
              <Wind className="w-8 h-8 text-[#1D3A4C]" strokeWidth={1.5} />
              <div className="mt-5 font-display text-2xl text-[#1D3A4C]">
                Nord → vai a Sud
              </div>
              <p className="text-sm text-[#5C7180] mt-2">
                Tramontana o Maestrale? Rifugiati sulle coste sud (Fetovaia,
                Cavoli, Lacona).
              </p>
            </div>
            <div className="rounded-2xl bg-white p-8 border border-[#1D3A4C]/10 hover:shadow-md transition-shadow">
              <Waves className="w-8 h-8 text-[#D96C4A]" strokeWidth={1.5} />
              <div className="mt-5 font-display text-2xl text-[#1D3A4C]">
                Sud → vai a Nord
              </div>
              <p className="text-sm text-[#5C7180] mt-2">
                Scirocco in arrivo? Sansone, Capo Bianco e Sorgente sono al
                riparo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DAYS OVERVIEW */}
      <section
        data-testid="days-overview-section"
        className="py-24 max-w-7xl mx-auto px-6 lg:px-10"
      >
        <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
          <div>
            <div className="font-accent text-xs text-[#D96C4A] mb-3">
              La settimana
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1D3A4C] max-w-2xl">
              Otto giorni, otto anime dell'isola
            </h2>
          </div>
          <Link
            to="/itinerario"
            data-testid="overview-view-all"
            className="text-sm text-[#D96C4A] hover:text-[#b8542f] inline-flex items-center gap-1 group"
          >
            Vedi tutto l'itinerario
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {DAYS.map((d, idx) => (
            <Link
              key={d.id}
              to={`/giorno/${d.id}`}
              data-testid={`overview-day-card-${d.id}`}
              className="group relative overflow-hidden rounded-2xl border border-[#1D3A4C]/10 bg-white hover:-translate-y-1 hover:shadow-xl transition-all duration-500"
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <div
                className="h-44 bg-cover bg-center relative"
                style={{ backgroundImage: `url(${d.cover})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-medium text-[#1D3A4C]">
                  Giorno {d.id}
                </div>
                <div className="absolute bottom-3 left-4 right-4 font-accent text-[10px] text-white/90">
                  {d.zone}
                </div>
              </div>
              <div className="p-5">
                <div className="text-xs text-[#8A9BA8]">
                  {d.day} · {formatDate(d.date)}
                </div>
                <div className="mt-2 font-display text-xl text-[#1D3A4C] leading-snug group-hover:text-[#D96C4A] transition-colors">
                  {d.title}
                </div>
                <div className="mt-4 pt-4 border-t border-[#1D3A4C]/10">
                  <WeatherWidget
                    lat={d.coords.lat}
                    lng={d.coords.lng}
                    date={d.date}
                    compact
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-[#1D3A4C] p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center">
            <div className="absolute -right-10 -bottom-16 opacity-10">
              <Sailboat className="w-72 h-72 text-white" strokeWidth={1} />
            </div>
            <div className="relative">
              <div className="font-accent text-xs text-[#F4C89F] mb-3">
                Il tuo diario
              </div>
              <h3 className="font-display text-4xl text-white leading-tight">
                Ogni tramonto merita di essere scritto.
              </h3>
              <p className="text-white/70 mt-4 max-w-md">
                Annota le spiagge preferite, i piatti indimenticabili, le
                fotografie mentali. Tutto salvato sul tuo browser, sempre con te.
              </p>
            </div>
            <div className="relative flex md:justify-end">
              <Link
                to="/diario"
                data-testid="home-diary-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#D96C4A] hover:bg-[#b8542f] text-white font-medium transition-all hover:-translate-y-0.5"
              >
                Apri il diario di viaggio
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
